import dayjs from 'dayjs';
import {
  BoxType,
  BulkShipmentType as BulkShipmentPayloadType,
  ProductType,
  ShipmentByIdType,
  ShipperConsigneeIdType,
  UserType,
} from '../store/reducers/Shipment/Type';

import {
  BulkShipmentType,
  PackageType,
  PickupDataType,
  ShipmentDataType,
  userDetailsType,
  GetShipmentPayloadType,
  BoxType as BoxTypePayload,
} from '../types/shipment';
import { resolveShipmentValueInEur, getEuComplianceRule } from '../utils/euCompliance';
import { isUSDestination, buildCpscPayload, normalizeCpscDisclaimCode, parseBoolean } from '../utils/usCompliance';
import { UsCpscComplianceType } from '../types/shipment';

export function getAddressObj(address: userDetailsType) {
  const { altContactName, altContactNo, altContactTelephoneCode } = address;
  const obj: UserType = {
    companyName: address.companyName,
    line1: address.line1,
    line2: address.line2,
    city: address.city,
    state: address.state,
    country: address.country,
    pincode: address.pincode,
    name: address.name,
    email: address.email,
    phone: {
      telephoneCode: address.telephoneCode,
      number: address.phoneNumber,
    },
  };
  if (altContactName && altContactNo && altContactTelephoneCode) {
    obj.alternatePhone = {
      telephoneCode: address.altContactTelephoneCode || '',
      number: address.altContactNo || '',
      name: address.altContactName || '',
    };
  }
  return obj;
}

// export function getSingleShipmentPackagePayload({
//   boxes,
//   packageData,
//   shipmentType,
//   pickupType,
// }: {
//   boxes: BoxType[];
//   packageData: PackageType;
//   shipmentType: ShipmentDataType;
//   pickupType: PickupDataType;
// }): PackageType {
//   console.info({ boxes });
//   const boxesTemp: BoxType[] = [...boxes];
//   const packageObj: PackageType = {
//     ...packageData,
//     unitOfMeasurement: shipmentType.unit,
//     currency: shipmentType.currency,
//     shipmentMode: shipmentType?.shipmentMode,
//     invoiceNumber: shipmentType.invoiceNumber || '',
//     userReferenceNumber: shipmentType.referenceNumber || '',
//     isDocument: shipmentType.type === 'document',
//   };
//   if (pickupType?.type === 'pickUp') {
//     packageObj.timeSlot = pickupType.timeSlot;
//     packageObj.pickupDate = pickupType.date ? `${pickupType.date}` : null;
//     packageObj.isDropOff = false;
//   }
//   const packageBoxes = boxesTemp.map(item => {
//     const products = item.products.map((product: ProductType) => {
//       const copyProduct: ProductType = { ...product };
//       delete copyProduct.actions;
//       delete copyProduct.id;
//       delete copyProduct.totalValue;
//       // product.value = product.unitValue;
//       return product;
//     });
//     // delete item.id;
//     delete item.selected;
//     delete item.startCount;
//     return <BoxTypePayload>{
//       ...item,
//       products,
//     };
//   });
//   packageObj.box = packageBoxes;
//   return packageObj;
// }

export function getSingleShipmentPackagePayload({
  boxes,
  packageData,
  shipmentType,
  pickupType,
}: {
  boxes: BoxType[];
  packageData: PackageType;
  shipmentType: ShipmentDataType;
  pickupType: PickupDataType;
}): PackageType {
  const boxesTemp: BoxType[] = [...boxes];

  const packageObj: PackageType = {
    ...packageData,
    unitOfMeasurement: shipmentType.unit,
    currency: shipmentType.currency,
    shipmentMode: shipmentType?.shipmentMode,
    invoiceNumber: shipmentType.invoiceNumber || '',
    isDocument: shipmentType.type === 'document',
    incoterm: shipmentType?.incoterm || packageData?.incoterm || 'FOB',
    freight:
      shipmentType?.freight !== undefined
        ? Number(shipmentType.freight)
        : packageData?.freight !== undefined
        ? Number(packageData.freight)
        : 0,
    freightInsurance:
      shipmentType?.freightInsurance !== undefined
        ? Number(shipmentType.freightInsurance)
        : packageData?.freightInsurance !== undefined
        ? Number(packageData.freightInsurance)
        : 0,
    iossNumber: shipmentType?.iossNumber
      ? String(shipmentType.iossNumber).trim()
      : packageData?.iossNumber
      ? String(packageData.iossNumber).trim()
      : '',
    iorEoriNumber: shipmentType?.iorEoriNumber
      ? String(shipmentType.iorEoriNumber).trim()
      : packageData?.iorEoriNumber
      ? String(packageData.iorEoriNumber).trim()
      : '',
  };

  delete (packageObj as any).userReferenceNumber;
  delete (packageObj as any).shipmentMode;
  delete (packageObj as any).recipientVatTaxId;

  if (pickupType?.type === 'pickUp') {
    packageObj.timeSlot = pickupType.timeSlot;
    packageObj.pickupDate = pickupType.date ? `${pickupType.date}` : null;
    packageObj.isDropOff = false;
  }

  const packageBoxes = boxesTemp.map(item => {
    const length = item.length === 0 ? null : item.length;
    const width = item.width === 0 ? null : item.width;
    const height = item.height === 0 ? null : item.height;

    const products = item.products.map((product: ProductType) => {
      const rawCpsc = product.cpscCompliance || product.compliance?.us;
      const cpscCompliance = rawCpsc ? buildCpscPayload(rawCpsc, true, product) : undefined;

      const copyProduct: ProductType = {
        ...product,
        productId: product.productId ? String(product.productId).trim() : '',
        productIdType: product.productIdType ? String(product.productIdType).trim() : 'SKU',
        merchantProductId: product.merchantProductId ? String(product.merchantProductId).trim() : '',
        standardManufacturerProductId:
          product.standardManufacturerProductId && String(product.standardManufacturerProductId).trim() !== ''
            ? String(product.standardManufacturerProductId).trim()
            : 'NO',
        nonStandardManufacturerProductId: product.nonStandardManufacturerProductId
          ? String(product.nonStandardManufacturerProductId).trim()
          : '',
        ...(cpscCompliance ? { cpscCompliance } : {}),
        compliance: {
          ...(product.compliance || {}),
          ...(cpscCompliance ? { us: cpscCompliance } : {}),
          ...(product.merchantProductId ||
          product.standardManufacturerProductId ||
          product.nonStandardManufacturerProductId
            ? {
                eu: {
                  merchantProductId: product.merchantProductId ? String(product.merchantProductId).trim() : '',
                  standardManufacturerProductId:
                    product.standardManufacturerProductId && String(product.standardManufacturerProductId).trim() !== ''
                      ? String(product.standardManufacturerProductId).trim()
                      : 'NO',
                  nonStandardManufacturerProductId: product.nonStandardManufacturerProductId
                    ? String(product.nonStandardManufacturerProductId).trim()
                    : '',
                },
              }
            : {}),
        },
      };
      if (!cpscCompliance) {
        delete copyProduct.cpscCompliance;
        if (copyProduct.compliance?.us) {
          delete copyProduct.compliance.us;
        }
      }
      if (
        !product.merchantProductId &&
        !product.standardManufacturerProductId &&
        !product.nonStandardManufacturerProductId
      ) {
        if (copyProduct.compliance?.eu) {
          delete copyProduct.compliance.eu;
        }
      }
      delete copyProduct.actions;
      delete copyProduct.id;
      delete copyProduct.totalValue;
      return copyProduct;
    });

    delete item.selected;
    delete item.startCount;

    return <BoxTypePayload>{
      ...item,
      length,
      width,
      height,
      products,
    };
  });

  packageObj.box = packageBoxes;
  return packageObj;
}

export function getShipmentPayload({
  shipments,
  isDropOff,
  pickupDate,
  timeSlot,
  warehouseId,
  warehouseAddress,
}: GetShipmentPayloadType) {
  const payload: BulkShipmentPayloadType[] = [];
  shipments.map((shipment: BulkShipmentType) => {
    const destCountry = shipment.consignee?.country || '';
    const currency = shipment.package?.currency || 'INR';
    const totalShipmentVal = shipment.package?.totalShipmentValue;
    const boxesTemp: BoxType[] = [...shipment.package.box];
    const shipmentValueEur = resolveShipmentValueInEur(boxesTemp, currency, totalShipmentVal);
    const shipmentMode = shipment.package?.shipmentMode || '';
    const euRule = getEuComplianceRule(destCountry, shipmentMode, shipmentValueEur);

    shipment.package.box = boxesTemp.map(item => {
      const products = item.products.map((product: ProductType) => {
        delete product.actions;
        delete product.id;
        delete product.totalValue;
        product.value = product.unitValue;
        product.productId = product.productId ? String(product.productId).trim() : '';
        product.productIdType = product.productIdType ? String(product.productIdType).trim() : 'SKU';

        let merchantProductId = product.merchantProductId ? String(product.merchantProductId).trim() : '';
        let standardManufacturerProductId = product.standardManufacturerProductId
          ? String(product.standardManufacturerProductId).trim()
          : '';
        let nonStandardManufacturerProductId = product.nonStandardManufacturerProductId
          ? String(product.nonStandardManufacturerProductId).trim()
          : '';

        standardManufacturerProductId = standardManufacturerProductId !== '' ? standardManufacturerProductId : 'NO';
        if (!euRule.showEuComplianceFields) {
          merchantProductId = '';
          nonStandardManufacturerProductId = '';
        }

        product.merchantProductId = merchantProductId;
        product.standardManufacturerProductId = standardManufacturerProductId;
        product.nonStandardManufacturerProductId = nonStandardManufacturerProductId;

        const isUS = isUSDestination(destCountry);
        const rawCpsc = product.cpscCompliance || product.compliance?.us;
        const cpscCompliance = isUS && rawCpsc ? buildCpscPayload(rawCpsc, true, product) : undefined;
        if (cpscCompliance) {
          product.cpscCompliance = cpscCompliance;
          product.compliance = {
            ...(product.compliance || {}),
            us: cpscCompliance,
          };
        } else {
          delete product.cpscCompliance;
          if (product.compliance?.us) {
            delete product.compliance.us;
          }
        }
        if (!euRule.showEuComplianceFields && product.compliance?.eu) {
          delete product.compliance.eu;
        }

        return product;
      });
      // delete item.id;
      delete item.selected;
      delete item.startCount;
      return <BoxTypePayload>{
        ...item,
        products,
      };
    });
    const packageWithNewFields = {
      ...shipment.package,
      isDropOff,
      incoterm: shipment.package?.incoterm || 'FOB',
      freight: Number(shipment.package?.freight || 0),
      freightInsurance: Number(shipment.package?.freightInsurance || 0),
      iossNumber: shipment.package?.iossNumber ? String(shipment.package.iossNumber).trim() : '',
      iorEoriNumber: shipment.package?.iorEoriNumber ? String(shipment.package.iorEoriNumber).trim() : '',
      ...(pickupDate && timeSlot ? { pickupDate, timeSlot } : {}),
    };
    shipment.package = packageWithNewFields;
    payload.push({
      ...shipment,
      shipper: getAddressObj(shipment.shipper),
      consignee: {
        ...getAddressObj(shipment.consignee),
        vat: String(shipment.consignee?.vat || (shipment.package as any)?.recipientVatTaxId || '').trim(),
      },
      warehouseId,
      warehouseAddress,
    });
  });
  return payload;
}

export function getUserDetailsStateObj(data: ShipperConsigneeIdType) {
  const { phone, alternatePhone, name, companyName, line1, line2, city, state, country, pincode, email } = data;
  const temp: userDetailsType = {
    name,
    companyName: companyName ? companyName : '',
    line1,
    line2: line2 ? line2 : '',
    city,
    state,
    country,
    pincode,
    phoneNumber: phone.number,
    telephoneCode: phone.telephoneCode,
    altContactTelephoneCode: alternatePhone?.telephoneCode ?? '',
    altContactNo: alternatePhone?.number ?? '',
    email,
    altContactName: alternatePhone?.name ?? '',
  };
  return temp;
}

export function getBoxStateObj(box: BoxType[]) {
  return box.length
    ? box.map(({ length, width, height, weight, products, boxCopyCount }, ind) => ({
        length,
        width,
        height,
        weight,
        id: ind.toString(),
        products:
          products?.map((p: any, pIdx) => {
            const rawCpsc = p.cpscCompliance || p.compliance?.us || p.cpsc;
            let cpscCompliance: UsCpscComplianceType | undefined = undefined;
            if (rawCpsc) {
              cpscCompliance = {
                beingSold: parseBoolean(rawCpsc.beingSold) ?? true,
                disclaimCode: normalizeCpscDisclaimCode(rawCpsc.disclaimCode),
                additionalContext: rawCpsc.additionalContext || '',
                certificateFiled: parseBoolean(rawCpsc.certificateFiled),
                productIdRegistry: rawCpsc.productIdRegistry || '',
                certificateVersionId: rawCpsc.certificateVersionId || '',
                certifierId: rawCpsc.certifierId || '',
                productId: rawCpsc.productId || '',
                productIdType: rawCpsc.productIdType || 'SKU',
              };
            }
            return {
              ...p,
              id: pIdx.toString(),
              productId: p.productId ? String(p.productId).trim() : '',
              productIdType: p.productIdType ? String(p.productIdType).trim() : 'SKU',
              merchantProductId: p.merchantProductId ? String(p.merchantProductId).trim() : '',
              standardManufacturerProductId: p.standardManufacturerProductId
                ? String(p.standardManufacturerProductId).trim()
                : '',
              nonStandardManufacturerProductId: p.nonStandardManufacturerProductId
                ? String(p.nonStandardManufacturerProductId).trim()
                : '',
              ...(cpscCompliance ? { cpscCompliance } : {}),
            };
          }) || [],
        selected: ind == 0,
        boxCopyCount,
      }))
    : [];
}

export function getPackageStateObj(data: ShipmentByIdType['_packageId'], defaultCurrency: string) {
  const {
    unitOfMeasurement,
    packaging,
    insurance,
    isDocument,
    isCod,
    isDropOff,
    pickupDate,
    timeSlot,
    codAmount,
    currency,
    box,
    userReferenceNumber,
    invoiceNumber,
  } = data;
  const temp: PackageType = {
    unitOfMeasurement,
    packaging,
    insurance,
    isDocument,
    isCod,
    isDropOff,
    pickupDate: pickupDate ? dayjs(pickupDate) : null,
    timeSlot,
    codAmount,
    currency: defaultCurrency || currency,
    box: [...getBoxStateObj(box)],
    totalBoxWeight: 'Need calc',
    invoiceNumber: invoiceNumber || '',
    userReferenceNumber: userReferenceNumber || '',
  };
  return temp;
}

export function getPickupStateObj(data: ShipmentByIdType['_packageId']) {
  const { isDropOff, pickupDate, timeSlot } = data;
  const temp: PickupDataType = {
    type: isDropOff ? 'dropOff' : 'pickUp',
    date: isDropOff ? undefined : dayjs(pickupDate),
    timeSlot: isDropOff ? '' : timeSlot,
  };
  return temp;
}

export function getShipmentTypeStateObj(data: ShipmentByIdType, currency: string) {
  const anyData = data as any;
  const temp: ShipmentDataType = {
    numberOfBoxes: data._packageId.box.length.toString(),
    shipmentMode: data.shipmentMode,
    currency: currency || data._packageId.currency,
    type: data._packageId.isDocument ? 'document' : 'parcel',
    unit: data._packageId.unitOfMeasurement,
    invoiceNumber: '',
    referenceNumber: '',
    dutyCharge: data.dutyCharge || '',
    incoterm: anyData?.incoterm || anyData?._packageId?.incoterm || anyData?.extraDetails?.other?.incoterm || 'FOB',
    freight: anyData?.freight ?? anyData?._packageId?.freight ?? anyData?.extraDetails?.other?.freight ?? 0,
    freightInsurance:
      anyData?.freightInsurance ??
      anyData?._packageId?.freightInsurance ??
      anyData?.extraDetails?.other?.freightInsurance ??
      0,
    iossNumber:
      anyData?.iossNumber || anyData?._packageId?.iossNumber || anyData?.extraDetails?.other?.iossNumber || '',
    recipientVatTaxId:
      anyData?.consignee?.vat ||
      anyData?._consigneeId?.vat ||
      anyData?.recipientVatTaxId ||
      anyData?._packageId?.recipientVatTaxId ||
      anyData?.extraDetails?.other?.recipientVatTaxId ||
      '',
    iorEoriNumber:
      anyData?.iorEoriNumber ||
      anyData?._packageId?.iorEoriNumber ||
      anyData?.extraDetails?.other?.ior ||
      anyData?.extraDetails?.other?.iorEoriNumber ||
      '',
  };
  return temp;
}
